import type { DataGridRowSelectionState, TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ConversationPipeline,
  type ExpertReportPipelineScope,
  useRunConversationsPipeline,
} from '@/entities/search';
import { toastError, toastSuccess } from '@/shared/lib';
import { MASS_RUN_SEARCH_PAGE_SIZE, MASS_RUN_SELECT_ALL_LIMIT } from './constants';
import type { DialogFetchData } from './types';

/** Параметры режима массовых действий над списком диалогов */
type Params = {
  /** Массовый запуск доступен только под разрешением `ADMIN_CONVERSATION_PIPELINE` */
  canRunPipeline: boolean;
  /**
   * Состав отмеченных строк ведёт сам раздел. Тогда отметки не сбрасываются сменой состава списка
   * и остаются тем же набором, который раздел сохраняет в выборку.
   */
  selectionOverride?: {
    rowSelection: DataGridRowSelectionState;
    onRowSelectionChange: (next: DataGridRowSelectionState) => void;
  };
  /** Ленивая загрузка среза выборки — тем же путём, что и список */
  fetchData: DialogFetchData;
  /** Действующие фильтры выборки (с наложенным слоем перехода) */
  filters: TableFilterState;
  /** Действующая сортировка выборки */
  sorting: TableSortingState;
  /** Ключ состава списка: его смена обесценивает ранее сделанный выбор строк */
  listCompositionKey: string;
};

/** Состояние и обработчики режима массовых действий */
type Result = {
  /** Режим массовых действий раскрыт: под разрешением и при нажатой кнопке «Действия» */
  isMassActionsActive: boolean;
  /** Единый состав отмеченных строк (поимённый выбор и «выбрать всё») */
  rowSelection: DataGridRowSelectionState;
  /** Число отмеченных диалогов */
  selectedCount: number;
  /** Отмечена хотя бы одна строка — есть что запускать */
  hasPipelineSelection: boolean;
  /** Идёт постраничный набор id для «выбрать всё» */
  isPreparingSelection: boolean;
  /** Идёт запрос массового запуска обработки */
  isRunPipelinePending: boolean;
  onToggleMassActions: () => void;
  onRowSelectionChange: (next: DataGridRowSelectionState) => void;
  onRunPipeline: (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => void;
};

/**
 * Режим массовых действий над списком диалогов: раскрытие блока действий, единый состав отмеченных
 * строк (поимённый выбор и «выбрать всё» в пределах предела массового запуска) и сам запуск
 * обработки по отмеченным диалогам.
 *
 * «Выбрать всё» материализует id первых `MASS_RUN_SELECT_ALL_LIMIT` диалогов текущей выборки в тот
 * же `rowSelection`, поэтому запуск всегда уходит списком id как есть. Смена состава списка снимает
 * весь выбор: отмеченные id больше не соответствуют показанной выборке.
 */
export const useMassActions = ({
  canRunPipeline,
  selectionOverride,
  fetchData,
  filters,
  sorting,
  listCompositionKey,
}: Params): Result => {
  const { t } = useTranslation(['dialogs']);
  const { mutate: runPipeline, isPending: isRunPipelinePending } = useRunConversationsPipeline();

  // Режим массовых действий раскрывается кнопкой «Действия»: только в нём видны столбец выбора
  // в строках и блок действий под строкой поиска.
  const [isMassActionsOpen, setIsMassActionsOpen] = useState(false);

  // Массовый выбор диалогов: поимённый выбор строк и «выбрать всё» хранятся единым составом
  // отмеченных id — «выбрать всё» материализует id первых диалогов выборки в тот же rowSelection.
  const [ownRowSelection, setOwnRowSelection] = useState<DataGridRowSelectionState>({});
  const rowSelection = selectionOverride?.rowSelection ?? ownRowSelection;
  const setRowSelection = selectionOverride?.onRowSelectionChange ?? setOwnRowSelection;
  // Идёт набор id для «выбрать всё» — блок действий занят до его завершения.
  const [isPreparingSelection, setIsPreparingSelection] = useState(false);
  // Актуальный состав выбора для обработчика мастер-чекбокса: сравнение до/после без устаревшего замыкания.
  const rowSelectionRef = useRef(rowSelection);
  rowSelectionRef.current = rowSelection;
  // Свой состав отметок обесценивается сменой состава списка, чужой — нет: раздел ведёт его сам.
  const isOwnSelection = selectionOverride == null;

  // Отмеченные диалоги — единый состав для поимённого выбора и «выбрать всё».
  const selectedConversationIds = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([rowId]) => Number(rowId)),
    [rowSelection],
  );

  const isMassActionsActive = canRunPipeline && isMassActionsOpen;
  const hasPipelineSelection = selectedConversationIds.length > 0;

  // Смена состава списка (фильтры/поиск/сортировка) обесценивает ранее сделанный выбор строк
  // и режим «выбрать все»: выбранные id больше не соответствуют показанной выборке. Состав,
  // который ведёт сам раздел, это не касается — там отметки набирают разными отборами.
  useEffect(() => {
    if (isOwnSelection) setOwnRowSelection({});
  }, [listCompositionKey, isOwnSelection]);

  // Кнопка «Действия»: раскрывает/сворачивает режим; сворачивание снимает свой массовый выбор.
  const handleToggleMassActions = useCallback(() => {
    setIsMassActionsOpen((open) => {
      if (open && isOwnSelection) setOwnRowSelection({});
      return !open;
    });
  }, [isOwnSelection]);

  // «Выбрать всё»: набираем id первых MASS_RUN_SELECT_ALL_LIMIT диалогов текущей выборки (тем же
  // срезом, что и список: действующие поиск/фильтры/сортировка) и отмечаем их как выбранные. Поиск
  // отдаёт не более страницы за запрос, поэтому предел набирается постранично.
  const selectAllUpToLimit = useCallback(async () => {
    setIsPreparingSelection(true);
    try {
      const next: DataGridRowSelectionState = {};
      let count = 0;
      let total = 0;
      let offset = 0;
      while (count < MASS_RUN_SELECT_ALL_LIMIT) {
        const pageLimit = Math.min(MASS_RUN_SEARCH_PAGE_SIZE, MASS_RUN_SELECT_ALL_LIMIT - count);
        const page = await fetchData({ offset, limit: pageLimit, filters, sorting });
        total = page.total;
        for (const item of page.items) {
          next[String(item.id)] = true;
          count += 1;
        }
        offset += page.items.length;
        // Выборка исчерпана — следующих страниц нет
        if (offset >= total || page.items.length === 0) break;
      }
      setRowSelection(next);
    } catch {
      toastError(t('dialogs:dialogListSection.runPipeline.error'));
    } finally {
      setIsPreparingSelection(false);
    }
  }, [fetchData, filters, sorting, setRowSelection, t]);

  // Изменение мульти-выбора DataGrid. Мастер-чекбокс кита выделяет разом все строки во вьюпорте
  // (>1 строки за жест) — такой массовый жест трактуем как «выбрать всю текущую выборку в пределах
  // предела», массовое снятие — как очистку. Изменение ровно одной строки — поимённый выбор.
  const handleRowSelectionChange = useCallback(
    (next: DataGridRowSelectionState) => {
      if (isPreparingSelection) return;
      const prev = rowSelectionRef.current;
      const addedCount = Object.keys(next).filter((id) => next[id] && !prev[id]).length;
      const removedCount = Object.keys(prev).filter((id) => prev[id] && !next[id]).length;
      if (addedCount > 1) {
        // Мастер-чекбокс «выбрать всё»: сразу собираем всю выборку до предела и выставляем итоговый
        // состав разом, не отмечая промежуточно только видимый вьюпорт. Иначе счётчик мигает числом
        // видимых строк (например 25), а через пару секунд — итоговым (500/N). На время сбора блок
        // действий показывает индикатор ожидания.
        void selectAllUpToLimit();
        return;
      }
      if (removedCount > 1) {
        setRowSelection({});
        return;
      }
      setRowSelection(next);
    },
    [isPreparingSelection, selectAllUpToLimit, setRowSelection],
  );

  // Массовый запуск обработки по отмеченным диалогам. Выборка уже материализована составом id
  // (поимённо либо «выбрать всё» в пределах предела), поэтому уходит списком id как есть.
  // Область запуска задаётся только у генерации заключений; без неё запуск прежний — целиком.
  const handleRunPipeline = useCallback(
    (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => {
      if (!hasPipelineSelection || isRunPipelinePending || isPreparingSelection) return;
      runPipeline(
        { filters: { conversationIds: selectedConversationIds }, pipeline, ...scope },
        {
          onSuccess: (result) => {
            // При усечении выборки по пределу подтверждение это отмечает.
            toastSuccess(
              result.truncated
                ? t('dialogs:dialogListSection.runPipeline.successTruncated', {
                    count: result.accepted,
                    limit: MASS_RUN_SELECT_ALL_LIMIT,
                  })
                : t('dialogs:dialogListSection.runPipeline.success', { count: result.accepted }),
            );
            // Свой выбор после запуска снимается; состав, который ведёт раздел, запуск не трогает —
            // это набор записей выборки, а не разовая отметка под действие.
            if (isOwnSelection) setOwnRowSelection({});
          },
        },
      );
    },
    [
      hasPipelineSelection,
      isRunPipelinePending,
      isPreparingSelection,
      selectedConversationIds,
      runPipeline,
      isOwnSelection,
      t,
    ],
  );

  return {
    isMassActionsActive,
    rowSelection,
    selectedCount: selectedConversationIds.length,
    hasPipelineSelection,
    isPreparingSelection,
    isRunPipelinePending,
    onToggleMassActions: handleToggleMassActions,
    onRowSelectionChange: handleRowSelectionChange,
    onRunPipeline: handleRunPipeline,
  };
};
